import React from 'react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { CHART_COLORS } from '@/lib/constants';

export function BreakdownChart({
  data = [],
  title = 'Breakdown',
  loading = false,
  nameKey = 'name',
  valueKey = 'count',
  emptyText = 'No data in this period',
  className,
}) {
  if (loading) {
    return (
      <Card className={className}>
        <CardHeader>
          <Skeleton className="h-5 w-32" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-48 w-full rounded-full mx-auto" style={{ maxWidth: 200 }} />
        </CardContent>
      </Card>
    );
  }

  // Format data for recharts
  const chartData = data.map((item) => ({
    name: item[nameKey] || item.name || item.code || 'Unknown',
    // The API returns ``count``; ``value`` is accepted for older callers.
    value: item[valueKey] ?? item.count ?? item.value ?? 0,
    percentage: item.percentage || 0,
  }));

  const renderCustomLabel = ({ name, percentage }) => {
    if (percentage < 5) return null;
    return `${percentage.toFixed(0)}%`;
  };

  return (
    <Card className={className} data-testid="breakdown-chart">
      <CardHeader>
        <CardTitle className="text-base font-medium">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {chartData.length === 0 ? (
          <div className="h-48 flex items-center justify-center text-sm text-muted-foreground text-center">
            {emptyText}
          </div>
        ) : (
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={40}
                outerRadius={70}
                paddingAngle={2}
                dataKey="value"
                label={renderCustomLabel}
                labelLine={false}
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={CHART_COLORS[index % CHART_COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  background: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px',
                }}
                formatter={(value, name) => [value.toLocaleString(), name]}
              />
              <Legend
                layout="vertical"
                align="right"
                verticalAlign="middle"
                iconType="circle"
                iconSize={8}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        )}
      </CardContent>
    </Card>
  );
}

export default BreakdownChart;
